import React, {
	useEffect,
	useMemo,
	useState,
	MouseEvent,
	useRef,
	useDeferredValue,
	useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState } from '../../stores/types/types';
import type { GraphicInterface } from '../../stores/types/graphics';
import Thumbnail from '../chat/chat-document-thumbnail';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import useMedia from '../../hooks/use-media';
import { getInitialGraphicDimensions } from '../../utils/custom-graphics';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import useDragItem from '../../hooks/use-dnd/use-drag-item';
import Colors from '../../utils/colors';
import { CLOUD_CONVERT_STATUS } from '../../types/constants';
import LoadingSpinner from '../general-components/loading-spinner';
import colors from '../../utils/colors';

interface Props {
	graphic: GraphicInterface;
	isHost: boolean;
	onDelete: Function;
	onDownload: React.MouseEventHandler<HTMLDivElement>;
	onDuplicate: (e: any) => void;
	onEdit: Function;
	onCancelConvert: (e: any) => void;
	user: any;
	isForShotModal: boolean;
	isOnCanvas: boolean;
	index: number;
	isDownloading?: boolean;
}

export default function GraphicsLink(props: Props) {
	const {
		graphic,
		isHost,
		onDelete,
		onDownload,
		onDuplicate,
		onEdit,
		onCancelConvert,
		user,
		isForShotModal,
		isOnCanvas,
		index,
		isDownloading = false,
	} = props;

	const {
		title,
		convert_status: convertStatus,
	} = graphic;

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const graphics = useSelector((state: GreenroomState) => state.GraphicsReducer.graphics);

	const cancelButtonRef = useRef(undefined as any);
	const [mouseY, setMouseY] = useState<string>('');
	const [showMenu, setShowMenu] = useState(false);
	const ellipsesOpen = useRef(false);
	// used to prevent both click listeners from firing at once
	const deferredShowMenu = useDeferredValue(showMenu);

	const {
		canEditOrControlMedia,
		canDeleteMedia,
	} = useMedia();

	const handleClick = () => {
		ellipsesOpen.current = false;
		setShowMenu(false);
	};

	const onScroll = () => {
		if (ellipsesOpen.current) {
			ellipsesOpen.current = false;
			setShowMenu(false);
		}
	};

	useEffect(() => {
		const scrollableDiv = document.getElementById('media-tab');
		if (scrollableDiv) {
			scrollableDiv.addEventListener('scroll', onScroll, false);
		}

		return () => {
			if (scrollableDiv) {
				scrollableDiv.removeEventListener('scroll', onScroll);
			}
		};
	}, []);

	const toggleEllipses = (e: MouseEvent<HTMLDivElement>) => {
		if (!ellipsesOpen.current) {
			let { pageY } = e;

			if (pageY + 100 > window.innerHeight) {
				// Search for: `Note => Green-3529`
				pageY = window.innerHeight - 137;
			} else {
				pageY = 40;
			}

			setMouseY(`${pageY}px`);
		}
		ellipsesOpen.current = !ellipsesOpen.current;
		setShowMenu(ellipsesOpen.current);
	};

	const [userCanEdit, userCanDelete] = useMemo(
		() => [
			canEditOrControlMedia(graphic, user, isHost, false),
			canDeleteMedia(graphic, user, isHost),
		],
		[isHost, graphic, user],
	);

	useEffect(() => {
		if (deferredShowMenu) {
			window.addEventListener('click', handleClick);
		}

		return () => {
			window.removeEventListener('click', handleClick);
		};
	}, [deferredShowMenu]);

	const handleDragAttempt = (): void => {
		// a new overlay that has finished on cloud convert will not have the initial scale values,
		// set it before adding to a shot
		const draggingGraphic = graphics.find((match: GraphicInterface) => match.id === graphic.id);
		if (draggingGraphic && (draggingGraphic?.scale_x === -1 || draggingGraphic.scale_y === -1)) {
			getInitialGraphicDimensions(draggingGraphic, graphics, token);
		}
	};

	const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
	};

	const isComplete = useMemo(
		() => !convertStatus || convertStatus === CLOUD_CONVERT_STATUS.COMPLETE,
		[convertStatus],
	);

	const canDrag = useMemo(
		() => (
			isHost
			&& ellipsesOpen.current === false
			&& isComplete
		),
		[isHost, ellipsesOpen.current, isComplete],
	);

	const { dragRef, isDragging } = useDragItem(
		{ id: graphic.id ?? 0, type: DRAG_TYPE.OVERLAY },
		canDrag,
	);
	/** double click handler - for quick access to the preview modal for a media item. */
	const quickMediaPreview = useCallback(() => {
		// validate permission for editing
		if (!userCanEdit) return;
		// open preview modal
		onEdit();
	}, [userCanEdit, graphic]);

	return (<>
		{isDragging && (<div className="media-link-drag-placeholder" />)}
		<div
			className={classNames('padding-top-small', {
				'link-is-highlighted': isOnCanvas,
				'link-is-dragging': isDragging,
			})}
		>
			<div
				id={`media-link-${index}`}
				className={classNames('media-link', {
					'ellipses-open': ellipsesOpen.current,
					'cursor-grab': canDrag,
					'no-shrink': isForShotModal,
					short: isDownloading,
				})}
				onFocus={(e) => e.currentTarget.blur()}
				ref={dragRef}
				onDoubleClick={quickMediaPreview}
				onMouseDown={handleDragAttempt}
				onDragStart={() => {
					setShowMenu(false);
					ellipsesOpen.current = false;
				}}
			>
				<div className="thumbnail">
					{convertStatus === CLOUD_CONVERT_STATUS.IN_PROGRESS
						? (
							<div className="processing-container">
								<Icon name={ICONS.CHECK_PROCESSING} color={COLORS.PALE_GREEN} size={27} />
							</div>
						) : (
							<div
								className="thumbnail-container"
								style={{ background: Colors.darkerGray }}
							>
								<Thumbnail thumbnail={graphic.thumbnail_url} />
							</div>
						)}
				</div>
				<div className="media-details">
					<div className='media-details-metadata'>
						<p className="media-title bl-font-s3">{title}</p>
						{convertStatus === CLOUD_CONVERT_STATUS.IN_PROGRESS && (
							<p className='media-description bl-font-c2'>Processing...</p>
						)}
					</div>
					{convertStatus === CLOUD_CONVERT_STATUS.IN_PROGRESS && userCanDelete
						&& <button ref={cancelButtonRef} className='delete-btn media-dots-wrap' onClick={onCancelConvert}>
							<Icon name={ICONS.TRASH} color={COLORS.GRAY} size={16}/>
						</button>
					}
					{isComplete && isHost && !isForShotModal && (
						isDownloading ? (
							<div className="media-download-spinner media-dots-wrap">
								<LoadingSpinner height={24} width={24} color={'#878793'} stroke={10}/>
							</div>
						) : (
							<div
								// If "media-dots-wrap" className changes,
								// modify getDeleteDialogPosition in media.tsx
								className="media-dots-wrap"
								onClick={toggleEllipses}
								onMouseDown={(e) => {
									stopPropagation(e);
								}}
							>
								<Icon name={ICONS.THREE_DOTS_HORIZONTAL} size={16} color={COLORS.WHITE}/>
							</div>
						)
					)}
				</div>

				<div className={classNames('media-ellipses-menu-container field-group select', { open: showMenu })}
					style={{
						position: mouseY === '40px' ? 'absolute' : 'fixed',
						right: mouseY === '40px' ? '42px' : '160px',
						top: `${mouseY}`,
					}}
					data-testid='media-ellipses-menu-container'
				>
					<div className={'media-ellipses-menu bl-font-p2'}>
						{userCanEdit && (
							<div
								className="media-ellipses-option select-option media-ellipses-menu-row"
								onClick={() => { onEdit(); }}
								onMouseDown={stopPropagation}
							>
								<div className="media-ellipses-menu-icon">
									<Icon name={ICONS.PREVIEW} size={12} color={COLORS.WHITE} />
								</div>
								<div className="media-ellipses-menu-text">Preview</div>
							</div>
						)}

						{userCanEdit && (
							<div
								className="media-ellipses-option select-option media-ellipses-menu-row"
								onClick={onDownload}
								onMouseDown={stopPropagation}
							>
								<div className="media-ellipses-menu-icon">
									<Icon name={ICONS.MEDIA_DOWNLOAD} size={12} color={COLORS.WHITE} />
								</div>
								<div className="media-ellipses-menu-text">Download</div>
							</div>
						)}

						{userCanEdit && (
							<div
								className="media-ellipses-option select-option media-ellipses-menu-row"
								onClick={onDuplicate}
								onMouseDown={stopPropagation}
							>
								<div className="media-ellipses-menu-icon">
									<Icon name={ICONS.MEDIA_DUPLICATE} size={12} color={COLORS.WHITE} />
								</div>
								<div className="media-ellipses-menu-text">Duplicate</div>
							</div>
						)}

						{typeof 1 === 'undefined' && userCanEdit && (
							<div
								className="media-ellipses-option select-option media-ellipses-menu-row"
								onClick={() => { }}
								onMouseDown={stopPropagation}
							>
								<div className="media-ellipses-menu-icon">
									<Icon name={ICONS.MEDIA_DUPLICATE} size={12} color={COLORS.WHITE} />
								</div>
								<div className="media-ellipses-menu-text">Duplicate</div>
							</div>
						)}

						{typeof 1 === 'undefined' && (
							<div
								className="media-ellipses-option select-option media-ellipses-menu-row"
								onClick={() => { }}
								onMouseDown={stopPropagation}
							>
								<div className="media-ellipses-menu-icon">
									<Icon name={ICONS.MEDIA_DOWNLOAD} size={12} color={COLORS.WHITE} />
								</div>
								<div className="media-ellipses-menu-text">Download</div>
							</div>
						)}

						{userCanDelete && (
							<div
								className="media-ellipses-option select-option critical-option media-ellipses-menu-row"
								style={{ borderTop: `1px solid ${colors.blGrayscale700}` }}
								onClick={() => onDelete()}
								onMouseDown={stopPropagation}
							>
								<div className="media-ellipses-menu-icon">
									<Icon name={ICONS.MEDIA_DELETE} size={12} color={COLORS.RED} />
								</div>
								<div className="media-ellipses-menu-text">Delete</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	</>);
}
